import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import '../styles/common.css';

function ChildrenManagement() {
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingChild, setEditingChild] = useState(null);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    pin: ''
  });

  useEffect(() => {
    // 로그인 상태 확인
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');

    if (!token || !userData) {
      navigate('/parent/login');
      return;
    }

    try {
      const parsedUser = JSON.parse(userData);
      if (parsedUser.type !== 'parent') {
        navigate('/');
        return;
      }
    } catch (error) {
      navigate('/parent/login');
      return;
    }

    fetchChildren();
  }, [navigate]);

  const fetchChildren = async () => {
    try {
      const response = await api.get('/families/children');
      setChildren(response.data.children || []);
    } catch (error) {
      setError('자녀 목록을 불러오는데 실패했습니다.');
      console.error('Fetch children error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      pin: ''
    });
    setEditingChild(null);
  };

  const handleAddChild = () => {
    resetForm();
    setShowAddModal(true);
  };

  const handleEditChild = (child) => {
    setFormData({
      name: child.name,
      pin: child.pin
    });
    setEditingChild(child);
    setShowAddModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingChild) {
        // 수정
        await api.patch(`/families/children/${editingChild.id}`, formData);
        alert('자녀 정보가 수정되었습니다.');
      } else {
        // 추가
        await api.post('/families/children', formData);
        alert('자녀가 추가되었습니다.');
      }

      setShowAddModal(false);
      resetForm();
      fetchChildren();
    } catch (error) {
      alert(error.response?.data?.error || '자녀 처리 중 오류가 발생했습니다.');
    }
  };

  const handleDeleteChild = async (childId, childName) => {
    if (!window.confirm(`'${childName}' 자녀를 삭제하시겠습니까?`)) {
      return;
    }

    try {
      await api.delete(`/families/children/${childId}`);
      alert('자녀가 삭제되었습니다.');
      fetchChildren();
    } catch (error) {
      alert(error.response?.data?.error || '자녀 삭제 중 오류가 발생했습니다.');
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="card">
          <div style={{ textAlign: 'center', padding: '20px' }}>
            로딩 중...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* 헤더 */}
      <div className="page-header">
        <Link to="/parent/dashboard" className="back-btn">← 뒤로가기</Link>
        <h1>👶 자녀 관리</h1>
        <button
          onClick={handleAddChild}
          className="btn btn-primary"
        >
          + 자녀 추가
        </button>
      </div>

      {/* 자녀 목록 */}
      <div className="children-container">
        {error && <div className="error">{error}</div>}

        {children.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">👶</div>
            <h3>아직 등록된 자녀가 없습니다</h3>
            <p>첫 번째 자녀를 등록해보세요!</p>
            <button
              onClick={handleAddChild}
              className="btn btn-primary"
            >
              자녀 등록하기
            </button>
          </div>
        ) : (
          <div className="children-grid">
            {children.map(child => (
              <div key={child.id} className="child-card">
                <div className="child-header">
                  <h3>{child.name}</h3>
                  <div className="child-pin">
                    PIN: {child.pin}
                  </div>
                </div>

                <div className="child-details">
                  <div className="detail-item">
                    <span className="detail-label">가입일:</span>
                    <span className="detail-value">
                      {new Date(child.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>

                <div className="child-actions">
                  <button
                    onClick={() => handleEditChild(child)}
                    className="btn btn-outline btn-sm"
                  >
                    수정
                  </button>
                  <button
                    onClick={() => handleDeleteChild(child.id, child.name)}
                    className="btn btn-danger btn-sm"
                  >
                    삭제
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 자녀 추가/수정 모달 */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingChild ? '자녀 정보 수정' : '새 자녀 추가'}</h2>
              <button
                onClick={() => setShowAddModal(false)}
                className="modal-close"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-group">
                <label className="form-label">자녀 이름 *</label>
                <input
                  type="text"
                  name="name"
                  className="form-input"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  maxLength="50"
                  placeholder="예: 김철수"
                />
              </div>

              <div className="form-group">
                <label className="form-label">PIN (1-6자리 숫자) *</label>
                <input
                  type="text"
                  name="pin"
                  className="form-input"
                  value={formData.pin}
                  onChange={handleInputChange}
                  required
                  pattern="[0-9]{1,6}"
                  placeholder="123456"
                />
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="btn btn-outline"
                >
                  취소
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                >
                  {editingChild ? '수정하기' : '추가하기'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        .page-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
          padding-bottom: 20px;
        }

        .page-header {
          background: white;
          padding: 20px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          display: flex;
          justify-content: space-between;
          align-items: center;
        }


        .page-header h1 {
          margin: 0;
          color: #333;
          font-size: 1.5rem;
        }

        .children-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 30px 20px;
        }

        .empty-state {
          text-align: center;
          padding: 60px 20px;
          background: white;
          border-radius: 15px;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
        }

        .empty-icon {
          font-size: 4rem;
          margin-bottom: 20px;
        }

        .empty-state h3 {
          color: #333;
          margin-bottom: 10px;
        }

        .empty-state p {
          color: #666;
          margin-bottom: 30px;
        }

        .children-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
          gap: 20px;
        }

        .child-card {
          background: white;
          border-radius: 15px;
          padding: 25px;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
          transition: transform 0.2s, box-shadow 0.2s;
        }

        .child-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0,0,0,0.15);
        }

        .child-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 15px;
        }

        .child-header h3 {
          margin: 0;
          color: #333;
          font-size: 1.2rem;
        }

        .child-pin {
          background: #8B4513;
          color: white;
          padding: 4px 12px;
          border-radius: 12px;
          font-size: 0.9rem;
          font-weight: bold;
        }

        .child-details {
          margin-bottom: 20px;
        }

        .detail-item {
          display: flex;
          justify-content: space-between;
          margin-bottom: 8px;
        }

        .detail-label {
          color: #888;
          font-size: 0.9rem;
        }

        .detail-value {
          color: #333;
          font-weight: 500;
        }

        .child-actions {
          display: flex;
          gap: 10px;
        }

        .btn-sm {
          padding: 8px 16px;
          font-size: 0.9rem;
        }

        .btn-danger {
          background: #ff4757;
          color: white;
          border: none;
        }

        .btn-danger:hover {
          background: #ff3742;
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
        }

        .modal-content {
          background: white;
          border-radius: 15px;
          width: 90%;
          max-width: 600px;
          max-height: 90vh;
          overflow-y: auto;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 25px;
          border-bottom: 1px solid #e0e0e0;
        }

        .modal-header h2 {
          margin: 0;
          color: #333;
        }

        .modal-close {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: #666;
        }

        .modal-form {
          padding: 25px;
        }

        .form-group {
          margin-bottom: 20px;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
        }

        .form-label {
          display: block;
          margin-bottom: 8px;
          color: #333;
          font-weight: 500;
        }

        .form-input {
          width: 100%;
          padding: 12px;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 1rem;
        }

        .modal-actions {
          display: flex;
          gap: 15px;
          justify-content: flex-end;
          margin-top: 30px;
        }

        @media (max-width: 768px) {
          .children-grid {
            grid-template-columns: 1fr;
          }

          .form-row {
            grid-template-columns: 1fr;
          }

          .page-header {
            flex-direction: column;
            gap: 15px;
          }
        }
      `}</style>
    </div>
  );
}

export default ChildrenManagement;
